
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Program } from '@/models'
import { useUserStore } from '@/stores/user'
import { Customer } from '@/models'
import Papa from 'papaparse'

@Component({
  name: 'FiltersDrawer'
})
export default class extends Vue {
  @Prop({ type: Boolean, default: false }) visible!: boolean
  @Prop({ type: Array, default: () => [] }) customers!: Customer[]
  
  userStore: any = useUserStore()
  showFilters = false

  programs: Program[] = []
  programsLoading = false

  emailDomains: { label: string; value: string }[] = []
  emailDomainsLoading = false

  businessEmailDomains: { label: string; value: string }[] = []
  businessEmailDomainsLoading = false

  listQuery: any = {
    sortByDate: 'desc',
    sortByUpdate: null,
    sortByName: null,
    page: 1,
    firstName: null,
    lastName: null,
    email: null,
    domain: null,
    businessEmail: null,
    businessEmailDomain: null,
    confirmed: null,
    approval: null,
    registrationFailureReason: null,
    dateRange: null,
    programId: null,
    zipcode: null,
    isRetired: null,
    lockedOnly: false
  }

  sortByDateOptions = [
    {
      label: this.$t('customers.filters.sortByDateOptions.desc'),
      value: 'desc'
    },
    {
      label: this.$t('customers.filters.sortByDateOptions.asc'),
      value: 'asc'
    }
  ]

  sortByUpdateOptions = [
    {
      label: this.$t('customers.filters.sortByUpdateOptions.desc'),
      value: 'desc'
    },
    {
      label: this.$t('customers.filters.sortByUpdateOptions.asc'),
      value: 'asc'
    }
  ]

  sortByNameOptions = [
    {
      label: this.$t('customers.filters.sortByNameOptions.asc'),
      value: 'asc'
    },
    {
      label: this.$t('customers.filters.sortByNameOptions.desc'),
      value: 'desc'
    }
  ]

  confirmedOptions = [
    {
      label: this.$t('customers.filters.confirmedOptions.notConfirmed'),
      value: false
    },
    {
      label: this.$t('customers.filters.confirmedOptions.confirmed'),
      value: true
    }
  ]

  approvalOptions = [
    {
      label: this.$t('customers.filters.approvalOptions.waiting'),
      value: 'waiting'
    },
    {
      label: this.$t('customers.filters.approvalOptions.verification_failed'),
      value: 'verification_failed'
    },
    {
      label: this.$t('customers.filters.approvalOptions.verified'),
      value: 'verified'
    }
  ]

  verificationFailedOptions = [
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.no_reason'),
      value: 'no_reason'
    },
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.bad_email'),
      value: 'bad_email'
    },
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.domain_unknown'),
      value: 'domain_unknown'
    },
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.catch_all_domain'),
      value: 'catch_all_domain'
    },
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.unknown_email'),
      value: 'unknown_email'
    },
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.risky_email'),
      value: 'risky_email'
    },
    {
      label: this.$t('customers.filters.registrationFailureReasonOptions.mailgun_failed'),
      value: 'mailgun_failed'
    }
  ]

  situationOptions = [
    {
      label: this.$t('customers.filters.situationOptions.employee'),
      value: false
    },
    {
      label: this.$t('customers.filters.situationOptions.retired'),
      value: true
    }
  ]

  get isCaretakerIEG(): boolean {
    return this.userStore.isCaretakerIEG
  }

  get filtersActive() {
    return Object.keys(this.listQuery).filter(i => {
      if (i === 'lockedOnly' && !this.listQuery[i]) return false

      if (this.listQuery[i] !== null && this.listQuery[i] !== '') {
        if ((typeof this.listQuery[i] === 'object' && this.listQuery[i].length > 0) || typeof this.listQuery[i] !== 'object') {
          if (i !== 'page') {
            return i
          }
        }
      }
    }).length
  }

  private created() {
    this.getPrograms()
    this.getEmailDomains()
    this.getBusinessEmailDomains()
  }

  changeSort(sortBy: string) {
    if (sortBy === 'date') {
      this.listQuery.sortByName = null
      this.listQuery.sortByUpdate = null
    } else if (sortBy === 'update') {
      this.listQuery.sortByName = null
      this.listQuery.sortByDate = null
    } else {
      this.listQuery.sortByUpdate = null
      this.listQuery.sortByDate = null
    }
  }

  private async getPrograms() {
    this.programsLoading = true

    const { data } = await Program
      .page(1)
      .per(100)
      .all()

    this.programs = data
    this.programsLoading = false
  }

  private async getEmailDomains() {
    this.emailDomainsLoading = true

    const { meta } = await Customer
      .stats({ email_domains: 'count' })
      .all()

    this.emailDomains = meta.stats.email_domains.count.domains.map((domain: string) => ({
      label: domain,
      value: domain
    }))
    this.emailDomainsLoading = false
  }

  private async getBusinessEmailDomains() {
    this.businessEmailDomainsLoading = true

    const { meta } = await Customer
      .stats({ business_email_domains: 'count' })
      .all()

    this.businessEmailDomains = meta.stats.business_email_domains.count.domains.map((domain: string) => ({
      label: domain,
      value: domain
    }))
    this.businessEmailDomainsLoading = false
  }

  resetFilters() {
    this.listQuery = {
      sortByDate: 'desc',
      sortByUpdate: null,
      sortByName: null,
      page: 1,
      firstName: null,
      lastName: null,
      email: null,
      domain: null,
      businessEmail: null,
      businessEmailDomain: null,
      confirmed: null,
      approval: null,
      registrationFailureReason: null,
      dateRange: null,
      programId: null,
      zipcode: null,
      isRetired: null,
      lockedOnly: false
    }

    this.refresh()
  }

  private validatePostalCode(value: string) {
    if (!value) {
      this.listQuery.zipcode = null
      return
    }

    // Nettoyage : on ne garde que les chiffres
    const cleanValue = value.replace(/\D/g, '')
    
    // On accepte soit 2 chiffres (département) soit 5 chiffres (code postal complet)
    if (cleanValue.length === 2 || cleanValue.length === 5) {
      this.listQuery.zipcode = cleanValue
    }
    // Sinon on conserve la valeur précédente déjà stockée dans listQuery.zipcode
  }

  refresh() {
    this.showFilters = false
    this.$emit('applyFilters', this.listQuery)
  }

  handleClose(): void {
    this.showFilters = false
  }

  handleSubmit(): void {
    this.$emit('applyFilters', this.listQuery)
    this.showFilters = false
  }
}
